import { ReactNode } from "react";
import { Box } from "@mui/material";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import { useTranslation } from "@localization/useTranslation";
import PolicyControlChip from "./PolicyControlChip/PolicyControlChip";
import { PolicyControl } from "./PolicySections.types";

export interface LinkedControlsBoxProps {
    controls: Array<PolicyControl>;
    onRemove?: (controlId: string) => void;
    headerAction?: ReactNode;
    children?: ReactNode;
    // Translation key for the box heading. The hosts write to different tables with different commit semantics
    // (policy/coverage links vs a mitigation plan's own scope), so they name the box accordingly.
    heading?: string;
    // "plain" blends into the surrounding form as one more bordered section; "highlight" is the purple callout
    // that sets the links apart from the policy text they sit under. Only the policy editor wants the callout,
    // so plain is the default and the one host that needs highlight asks for it.
    variant?: "plain" | "highlight";
    noBorder?: boolean;
    noPadding?: boolean;
}

// The "Tilknyttede kontroller" box: a small header (optionally with a trailing action such as an Add button)
// over the linked-control chips. Shared by the policy section editor and the risk assess/mitigation dialogs so
// the surface stays identical within each. Read-only (no chip remove) when onRemove is omitted.
function LinkedControlsBox(props: LinkedControlsBoxProps) {
    const { translateString } = useTranslation();
    const isHighlighted = props.variant === "highlight";

    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <DotLegalHeader headerStyle={"small"} marginBottom={0}>
                    {translateString(props.heading ?? "linkedControls")}
                </DotLegalHeader>
                {props.headerAction}
            </Box>
            {props.controls.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {props.controls.map((control) => (
                        <PolicyControlChip
                            key={control.id}
                            control={control}
                            subduedStroke={isHighlighted}
                            onRemove={props.onRemove ? () => props.onRemove!(control.id) : undefined}
                        />
                    ))}
                </Box>
            )}
            {props.children}
        </Box>
    );

    if (isHighlighted) {
        return (
            <Box
                sx={(theme) => ({
                    padding: 2,
                    background: "linear-gradient(90deg, #F8F6FE 0%, #F0EFFE 100%)",
                    borderBottomRightRadius: 12,
                    borderTopRightRadius: 12,
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                })}
            >
                {content}
            </Box>
        );
    }

    // DotLegalSection rather than a hand-rolled border, so the box is pixel-identical to the other sections of
    // the form it sits in. noMargin because the hosts own the spacing around it.
    return (
        <DotLegalSection noMargin noPadding={props.noPadding} noBorder={props.noBorder}>
            {content}
        </DotLegalSection>
    );
}

export default LinkedControlsBox;
